import { FilterDefinition, ModelPortfolio_TEMP, SearchRequest } from '@blk/aw-api-clients';
import { Aggregate, Portfolio } from '@blk/aw-bff-typescript-models';
import { ModelPortfolioModelType } from '@blk/awt-aladdinportfoliobuilderserver';
import { isArray, isEmpty } from 'lodash';
import moment from 'moment';
import { getLastValueFromPath } from './GeneralUtils';

export const getPostSearchPortfoliosParams = (category: 'accountID' | 'model' | 'benchmark'): SearchRequest => {
  const limit = 1000;
  let filters: FilterDefinition[] = [
    {
      field: {
        name: 'tag',
        type: 'string',
      },
      categories: [category],
    },
  ];
  const params: SearchRequest = {
    limit,
    filterParams: [
      {
        filters,
      },
    ],
  };
  return params;
};

// TODO AWM-14594 this is a temporary util function to convert ModelPortfolio_TEMP to Portfolio
export function convertModelPortfolioTEMPToPortfolio(
  modelPortfolio: ModelPortfolio_TEMP,
  defaultTag: 'model' | 'benchmark' | 'entitledModel'
): Portfolio;
export function convertModelPortfolioTEMPToPortfolio(
  modelPortfolio: ModelPortfolio_TEMP | undefined,
  defaultTag: 'model' | 'benchmark' | 'entitledModel'
): Portfolio | undefined;
export function convertModelPortfolioTEMPToPortfolio(
  modelPortfolio: ModelPortfolio_TEMP | undefined,
  defaultTag: 'model' | 'benchmark' | 'entitledModel'
): Portfolio | undefined {
  return modelPortfolio
    ? {
        // modelPortfolio.description is the model name
        name: modelPortfolio.description,
        positions: modelPortfolio.components.map((c, idx) => ({
          id: c.cusip + '-' + idx,
          cusip: c.cusip,
          weight: c.weight / 100,
          notionalWeight: c.weight / 100,
          marketValue: c.weight,
          notionalMarketValue: c.weight,
          posInfo: c.posInfo,
        })),
        currency: modelPortfolio.currency,
        openLots: [],
        closedLots: [],
        otherBenchmarks: [],
        mandateRestrictions: modelPortfolio?.mandateRestrictions ?? [],
        tag: modelPortfolio.type ?? defaultTag,
        origin: 'SYSTEM',
        id: modelPortfolio.id,
        creationTimestamp: modelPortfolio.date
          ? moment.utc(modelPortfolio.date, 'YYYYMMDD').startOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]')
          : moment().toISOString(),
        metadata: modelPortfolio.metadata ?? { family: modelPortfolio.family, provider: modelPortfolio.provider },
        otherModels: [],
      }
    : undefined;
}

export const convertPortfolioToModelPortfolio = (portfolio: Portfolio | undefined): ModelPortfolio_TEMP | undefined =>
  portfolio
    ? {
        id: portfolio.id,
        currency: portfolio.currency,
        components: portfolio.positions.map((p) => ({
          id: p.id,
          cusip: p.cusip,
          marketValue: p.marketValue,
          notionalMarketValue: p.notionalMarketValue,
          weight: (p.weight ?? p.marketValue ?? 0) * 100,
          notionalWeight: (p.notionalWeight ?? p.notionalMarketValue ?? 0) * 100,
          posInfo: p.posInfo,
        })),
        type:
          portfolio.tag === 'benchmark' || portfolio.tag === 'model' || portfolio.tag === 'entitledModel'
            ? (portfolio.tag as ModelPortfolioModelType)
            : 'benchmark',
        // description is optional, but it is used as display value in PortfolioSummaryBar
        description: portfolio.name ?? portfolio.id,
        mandateRestrictions: portfolio?.mandateRestrictions ?? [],
        metadata: portfolio.metadata,
        origin: portfolio.origin,
      }
    : undefined;

/**
 * Retrieves the calculated field from an AWPortfolio Portfolio or Aggregate based on an entity's configured displayField.
 * This helper assumes the displayField is of type 'metadata.*` and takes the last period delimited value.
 * It falls back to the portfolio's ID in all cases.
 *
 */
export function getDisplayValueForAWPortfolioObject(
  portfolio: Portfolio | Aggregate,
  displayField: string | undefined,
  isCaseInsensitive?: boolean,
  defaultValue?: string
): string;
export function getDisplayValueForAWPortfolioObject(
  portfolio: Portfolio | Aggregate,
  displayFields: string[],
  isCaseInsensitive?: boolean,
  defaultValue?: string
): string;
export function getDisplayValueForAWPortfolioObject(
  portfolio: Portfolio | Aggregate,
  displayFields: string | string[] | undefined,
  isCaseInsensitive: boolean = false,
  defaultValue?: string
): string {
  let value = defaultValue || portfolio.id;
  if (isEmpty(displayFields)) {
    return value;
  }
  const displayFieldsToIterate = isArray(displayFields) ? displayFields : [displayFields];
  const metadataKeys = Object.keys(portfolio.metadata);
  for (const displayField of displayFieldsToIterate) {
    if (displayField?.startsWith('metadata.')) {
      const field = getLastValueFromPath(displayField);
      const foundKey = isCaseInsensitive
        ? (metadataKeys.find((key) => key.toLowerCase() === field.toLowerCase()) ?? field)
        : field;
      const metaDataField = portfolio.metadata?.[foundKey];
      if (metaDataField) {
        return metaDataField;
      }
    }
  }
  return value;
}
